import { AgGridModule } from '@ag-grid-community/angular';
import { ColDef, ValueFormatterFunc } from '@ag-grid-community/core/dist/esm/es6/entities/colDef';
import { FirstDataRenderedEvent } from '@ag-grid-community/core/dist/esm/es6/events';
import { ModuleRegistry } from '@ag-grid-community/core/dist/esm/es6/modules/moduleRegistry';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model/dist/esm/es6/serverSideRowModelModule';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter/dist/esm/es6/setFilterModule';
import { StatusBarModule } from '@ag-grid-enterprise/status-bar/dist/esm/es6/statusBarModule';
import { formatDate } from '@angular/common';
import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseGrid } from 'shared/abstract/base-grid';
import { DateFloatingFilterComponent } from 'shared/components/ag-grid/filters/date-floating-filter/date-floating-filter.component';
import { DropdownFilterValue } from 'shared/components/ag-grid/filters/dropdown-floating-filter/dropdown-floating-filter.component';
import { ColoredPillTypesEnum } from 'shared/enums/colored-pill-types.enum';
import { CurrentSupplierUserInterface } from 'shared/interfaces/current-supplier-user.interface';
import { AccessTypeEnum, PermissionAccessZoneEnum } from 'shared/interfaces/permissions.interface';
import { ModalService, ToastService, UserApiService } from 'shared/services';
import { supplierUserHasAccess } from 'shared/util/supplierUserHasAccess';
import { InvitedUsersService } from 'users/services/invited-users.service';

@Component({
    selector: 'app-invited-users-table',
    templateUrl: './invited-users-table.component.html',
    styleUrls: ['./invited-users-table.component.scss'],
    imports: [AgGridModule, TranslateModule],
})
export class InvitedUsersTableComponent extends BaseGrid implements OnInit {
    private currentUserCanInviteUsers: boolean = false;

    //TODO column headers should be translated as well
    public columnDefs: ColDef[] = [
        {
            field: 'companyName',
            colId: 'companyName',
            hide: true,
            minWidth: 180,
            floatingFilter: true,
            unSortIcon: true,
            floatingFilterComponentParams: { suppressFilterButton: true },
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        {
            field: 'email',
            colId: 'email',
            minWidth: 250,
            floatingFilter: true,
            unSortIcon: true,
            floatingFilterComponentParams: { suppressFilterButton: true },
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        {
            headerName: 'Valid Until',
            colId: 'validUntil',
            field: 'validUntil',
            floatingFilter: true,
            floatingFilterComponent: DateFloatingFilterComponent,
            floatingFilterComponentParams: { suppressFilterButton: true },
            unSortIcon: true,
            valueFormatter: ((params) => {
                return formatDate(params.value, 'MM/dd/yyyy', this.translateService.getCurrentLang());
            }) as ValueFormatterFunc,
        },
        {
            colId: 'inviteStatus',
            field: 'inviteStatus',
            unSortIcon: true,
            width: 120,
            floatingFilterComponent: 'dropdownFloatingFilter',
            floatingFilter: true,
            filterParams: {
                values: [{ value: 'users.invitedUsers.active' }, { value: 'users.invitedUsers.expired' }] as DropdownFilterValue[],
            },
            floatingFilterComponentParams: {
                suppressFilterButton: true,
            },
            cellRenderer: 'coloredPillCellRenderer',
            cellRendererParams: {
                pillType: (params: string) => {
                    const pillTypesMap = new Map();

                    pillTypesMap.set('users.invitedUsers.active', ColoredPillTypesEnum.SUCCESS);
                    pillTypesMap.set('users.invitedUsers.expired', ColoredPillTypesEnum.WARNING);

                    return pillTypesMap.get(params);
                },
            },
            comparator: (valueA, valueB) => {
                return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
            },
        },
        {
            field: 'inviteLink',
            width: 100,
            headerComponent: 'resetFiltersHeader',
            cellRenderer: 'invitedUserLinkCellRenderer',
            headerComponentParams: { headerName: 'users.invitedUsers.inviteLink' },
            resizable: false,
            floatingFilter: false,
            sortable: false,
        },
        {
            colId: 'actions',
            filter: false,
            hide: true,
            resizable: false,
            sortable: false,
            minWidth: 110,
            maxWidth: 110,
            headerComponent: 'resetFiltersHeader',
            cellRenderer: 'actionButtonsCellRenderer',
            cellRendererParams: {
                alignment: 'right',
                buttons: [
                    {
                        clickAction: this.resendInvitation.bind(this),
                        icon: 'envelope',
                        computeVisibleState: this.isResendInvitationButtonVisible.bind(this),
                        tooltip: 'users.invitedUsers.resendInvitationTooltip',
                    },
                    {
                        clickAction: this.deleteInvitation.bind(this),
                        icon: 'trash',
                        tooltip: 'users.invitedUsers.deleteInvitationTooltip',
                    },
                ],
            },
        },
    ];

    constructor(
        router: Router,
        activatedRoute: ActivatedRoute,
        destroyRef: DestroyRef,
        private invitedUsersService: InvitedUsersService,
        public toastService: ToastService,
        public translateService: TranslateService,
        private modalService: ModalService,
        private userApiService: UserApiService,
    ) {
        ModuleRegistry.registerModules([ServerSideRowModelModule, StatusBarModule, SetFilterModule]);
        super(invitedUsersService, router, activatedRoute, destroyRef);
    }

    ngOnInit(): void {
        this.subscriptions.getLoginInfo = this.userApiService.getLoginInformation().subscribe((userInformation) => {
            if (userInformation.isShiji) {
                this.columnDefs[0].hide = false;
                this.agGrid?.api?.setColumnVisible('companyName', true);
                this.currentUserCanInviteUsers = true;
            } else {
                const internalPermissions = (userInformation as CurrentSupplierUserInterface).internalPermissions;
                this.currentUserCanInviteUsers = supplierUserHasAccess(
                    internalPermissions,
                    PermissionAccessZoneEnum.ACCESS_TO_USERS,
                    AccessTypeEnum.INVITE,
                );
            }
        });
    }

    override onFirstDataRendered(params: FirstDataRenderedEvent) {
        if (this.currentUserCanInviteUsers) {
            this.columnDefs[this.columnDefs.length - 1].hide = false;
            this.agGrid?.api?.setColumnVisible('actions', true);
            this.agGrid?.api?.getColumn('inviteLink')?.setColDef(
                {
                    field: 'inviteLink',
                    width: 100,
                    cellRenderer: 'invitedUserLinkCellRenderer',
                    headerComponentParams: { headerName: 'users.invitedUsers.inviteLink' },
                    resizable: false,
                    floatingFilter: false,
                    sortable: false,
                },
                null,
            );
        }
        super.onFirstDataRendered(params);
    }

    private resendInvitation(rowData: any) {
        this.subscriptions.resendConfirmationModal = this.modalService
            .openSimpleModal(
                this.translateService.instant('users.invitedUsers.reactivateInvitationModal.body', { email: rowData.email }),
                this.translateService.instant('users.invitedUsers.reactivateInvitationModal.title'),
                this.translateService.instant('users.invitedUsers.reactivateInvitationModal.yesButtonText'),
                this.translateService.instant('generalKeywords.cancel'),
                true,
                'top',
            )
            .onClose.subscribe((result) => {
                if (result) {
                    this.invitedUsersService.reactivateExpiredInvitation(rowData.id).subscribe(() => {
                        this.toastService.showToast('users.invitedUsers.resendSuccessToast', 'success', { email: rowData.email });
                        this.agGrid?.api.refreshServerSide({ purge: true });
                    });
                }
            });
    }

    private isResendInvitationButtonVisible(rowData: any): boolean {
        return rowData.inviteStatus.endsWith('expired');
    }

    private deleteInvitation(rowData: any) {
        this.modalService
            .openDeleteConfirmationModal(
                this.translateService.instant('users.invitedUsers.deleteModal.body'),
                this.translateService.instant('users.invitedUsers.deleteModal.title'),
                rowData.companyName,
                rowData.email,
                this.translateService.instant('users.invitedUsers.deleteModal.entityTypeName'),
            )
            .onClose.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => {
                if (result) {
                    this.invitedUsersService.deleteUserInvitation(rowData.id).subscribe(() => {
                        this.toastService.showToast('users.invitedUsers.deleteSuccessToast', 'success', { email: rowData.email });
                        this.agGrid?.api.refreshServerSide({ purge: true });
                    });
                }
            });
    }
}
